"use client";

import { cn } from "@newxsystems/shared";
import AutoScroll from "embla-carousel-auto-scroll";
import useEmblaCarousel from "embla-carousel-react";
import { useMemo } from "react";
import { resolveTootPrimitive } from "../../../../client";
import type { LogoProofBlockLogoInput } from "@newxsystems/types";
import type { ITootLogoStripProps } from "./TootLogoStrip.props";

const getBasis = (count: number) =>
  count === 6 ? "basis-1/2 sm:basis-1/3 lg:basis-1/4" : "basis-1/2 sm:basis-1/3 lg:basis-1/5";
const getGrid = (count: number) =>
  count <= 1
    ? "grid-cols-1"
    : count === 2
      ? "grid-cols-1 sm:grid-cols-2"
      : count === 3
        ? "grid-cols-1 sm:grid-cols-3"
        : count === 4
          ? "grid-cols-2 lg:grid-cols-4"
          : "grid-cols-2 md:grid-cols-3 lg:grid-cols-5";
const LogoTile = ({
  carousel = false,
  index,
  logo,
}: {
  carousel?: boolean;
  index: number;
  logo: LogoProofBlockLogoInput;
}) => {
  const Image = resolveTootPrimitive("Image");
  return (
    <div
      className={cn(
        "flex min-h-[5.5rem] items-center justify-center rounded-[var(--toot-radius-card)] border border-toot-grid bg-card p-2 sm:min-h-[6.5rem]",
        carousel
          ? "shadow-[0_8px_24px_rgba(23,25,29,0.07)]"
          : "group/logo shadow-[0_8px_24px_rgba(23,25,29,0.05)] transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:border-toot-orange/55 hover:shadow-[0_14px_32px_rgba(23,25,29,0.10)] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
      )}
    >
      <Image
        alt={logo.image.alternateText}
        aspectRatio="infographic"
        className="h-16 w-full max-w-[15rem] sm:h-[4.5rem] lg:h-20 lg:max-w-[17rem]"
        decoding="async"
        height={logo.image.height ?? undefined}
        imageClassName={cn(
          "h-full w-full !object-contain",
          carousel
            ? "opacity-100"
            : "opacity-80 grayscale contrast-125 transition-[filter,opacity] duration-300 group-hover/logo:grayscale-0 group-hover/logo:opacity-100 motion-reduce:transition-none",
        )}
        loading={index < 6 ? "eager" : "lazy"}
        sizes={logo.image.sizes ?? "(min-width: 1024px) 176px, (min-width: 640px) 28vw, 45vw"}
        src={logo.image.defaultSrc}
        srcSet={logo.image.srcSet ?? undefined}
        width={logo.image.width ?? undefined}
      />
    </div>
  );
};

export const TootLogoStrip = ({ className, label, logos }: ITootLogoStripProps) => {
  const items = logos ?? [];
  const autoScroll = useMemo(
    () =>
      AutoScroll({
        direction: "backward",
        playOnInit: true,
        speed: 0.8,
        startDelay: 0,
        stopOnInteraction: false,
        stopOnMouseEnter: false,
      }),
    [],
  );
  const plugins = useMemo(() => [autoScroll], [autoScroll]);
  const [emblaRef] = useEmblaCarousel({ align: "start", loop: true }, plugins);
  if (!items.length) return null;

  if (items.length <= 5)
    return (
      <div
        aria-label={label}
        className={cn("grid gap-3 sm:gap-4", getGrid(items.length), className)}
        role="list"
      >
        {items.map((logo, index) => (
          <div
            className={cn(
              items.length % 2 === 1 &&
                index === items.length - 1 &&
                "col-span-2 mx-auto w-full max-w-[20rem] md:col-span-1 md:mx-0 md:max-w-none",
            )}
            key={`${logo.image.defaultSrc}-${index}`}
            role="listitem"
          >
            <LogoTile index={index} logo={logo} />
          </div>
        ))}
      </div>
    );

  return (
    <div
      aria-label={label}
      aria-roledescription="carousel"
      className={cn("relative mx-auto w-full", className)}
      role="region"
    >
      <div className="overflow-hidden [contain:layout_paint]" ref={emblaRef}>
        <div className="-ml-3 flex py-1 will-change-transform sm:-ml-4">
          {items.map((logo, index) => (
            <div
              aria-roledescription="slide"
              className={cn("min-w-0 shrink-0 grow-0 pl-3 sm:pl-4", getBasis(items.length))}
              key={`${logo.image.defaultSrc}-${index}`}
              role="group"
            >
              <LogoTile carousel index={index} logo={logo} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

TootLogoStrip.displayName = "Content::TootLogoStrip";
